import { Injectable } from '@angular/core';
import { FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { catchError, forkJoin, lastValueFrom, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { FormValues } from '../dto/blower-payload.dto';
import { Field } from '../models/field.model';
import { NotificationType } from './notification.constants';
import { NotificationService } from './notification.service';
import { UnitConversionService } from './unit-conversion.service';

// Add the new type definition
export type MinMaxValues = { [key: string]: { min: number; max: number } };

// Options object for convertMinMax to keep parameter count low
export interface ConvertMinMaxOptions {
  field: Field;
  baseUnit: string;
  newUnit: string;
  valueType: string;
  originalMinMax: MinMaxValues;
  convertedMinMax: MinMaxValues;
  form: FormGroup;
}

// Context object for onUnitChange last parameter
export interface UnitChangeContext {
  selectedUnits: { [key: string]: string };
  originalMinMax: MinMaxValues;
  convertedMinMax: MinMaxValues;
}

@Injectable({
  providedIn: 'root',
})
export class DynamicFormBaseService {
  constructor(
    private unitConversionService: UnitConversionService,
    private notificationService: NotificationService,
  ) {}

  public buildValidators(field: Field): ValidatorFn[] {
    const validators: ValidatorFn[] = [];
    if (field?.required) {
      validators.push(Validators.required);
    }
    // Only add min validator when field.min is explicitly provided
    if (field && field.min !== undefined && field.min !== null) {
      validators.push(Validators.min(field.min));
    }

    // Only add max validator when field.max is explicitly provided
    if (field && field.max !== undefined && field.max !== null) {
      validators.push(Validators.max(field.max));
    }
    return validators;
  }

  initializeUnitIfNeeded(field: Field, selectedUnits: { [key: string]: string }): void {
    // Ensure the selectedUnits map has an entry for this field.
    if (!selectedUnits[field.name] && field?.baseUnit) {
      // Use explicit property access on the right-hand side to differ slightly from other copies
      selectedUnits[field.name] = field.baseUnit;
    }
  }

  storeOriginalMinMax(field: Field, originalMinMax: MinMaxValues): void {
    const hasOriginal = !!originalMinMax[field.name];
    if (!hasOriginal && (field.min !== undefined || field.max !== undefined)) {
      originalMinMax[field.name] = {
        min: field.min,
        max: field.max,
      };
    }
  }

  updateValidators(fieldName: string, min: number, max: number, form: FormGroup): void {
    const control = form.get(fieldName);
    if (control) {
      const nextValidators: ValidatorFn[] = [];
      if (min !== undefined && min !== null) {
        // Ensure numeric coercion
        nextValidators.push(Validators.min(Number(min)));
      }
      if (max !== undefined && max !== null) {
        nextValidators.push(Validators.max(Number(max)));
      }
      control.setValidators(nextValidators);
      control.updateValueAndValidity();
    }
  }

  async convertFormValuesToBaseUnits(
    formValues: FormValues,
    formFields: Field[],
    selectedUnits: { [key: string]: string },
  ): Promise<FormValues> {
    const convertedValues = { ...formValues };
    const conversionPromises: Promise<void>[] = [];

    for (const field of formFields) {
      const fieldName = field?.name;
      const baseUnit = field?.baseUnit;
      const currentUnit = selectedUnits[fieldName];
      const currentValue = formValues[fieldName];

      if (currentUnit !== baseUnit && currentValue !== undefined && currentValue !== null) {
        // Use lastValueFrom to interop with Observables and keep the parallel execution
        const conversionPromise = lastValueFrom(this.unitConversionService.convert(currentValue, currentUnit, baseUnit, field.category))
          .then((valueInBaseUnit) => {
            convertedValues[fieldName] = valueInBaseUnit;
          })
          .catch(() => {
            // Keep original value if conversion fails
            convertedValues[fieldName] = currentValue;
          });

        conversionPromises.push(conversionPromise);
      }
    }

    await Promise.all(conversionPromises);
    return convertedValues;
  }

  convertMinMax(options: ConvertMinMaxOptions): void {
    const { field, baseUnit, newUnit, valueType, originalMinMax, convertedMinMax, form } = options;
    const originalMin = originalMinMax[field.name]?.min;
    const originalMax = originalMinMax[field.name]?.max;

    // Skip conversion if values are undefined or null
    if ((originalMin === undefined && originalMax === undefined) || !baseUnit || !newUnit || !valueType) {
      return;
    }

    // Convert min/max values (if present) and normalize errors to preserve originals
    const min$ =
      originalMin !== undefined && originalMin !== null
        ? this.unitConversionService.convert(originalMin, baseUnit, newUnit, valueType).pipe(catchError(() => of(originalMin)))
        : of(undefined);

    const max$ =
      originalMax !== undefined && originalMax !== null
        ? this.unitConversionService.convert(originalMax, baseUnit, newUnit, valueType).pipe(catchError(() => of(originalMax)))
        : of(undefined);

    forkJoin({ convertedMin: min$, convertedMax: max$ }).subscribe(({ convertedMin, convertedMax }) => {
      // If the selected unit is not the base unit, for some fields we want to
      // enforce explicit min/max limits (provided by product requirements)
      // instead of using the converted base values. Match common field names
      // (camelCase) used in the form.
      const nonBaseOverrideMap: { [key: string]: { min: number; max: number } } = {
        // suction_air_temperature -> suctionAirTemperature
        suctionAirTemperature: { min: -22, max: 176 },
        // vacuum
        vacuum: { min: 0, max: 29.83 },
        // suction_volumetric_flow -> suctionVolumetricFlow
        suctionVolumetricFlow: { min: 0, max: 42377.6 },
        // altitude_from_sea_level -> altitudeFromSeaLevel
        altitudeFromSeaLevel: { min: 0, max: 11482.94 },
        // absolute_pressure_after_recovery -> absolutePressureAfterRecovery
        // For non-base units we want a much larger upper bound (user requested)
        absolutePressureAfterRecovery: { min: 14.77, max: 10000000 },
        // temperature_after_recovery -> temperatureAfterRecovery
        temperatureAfterRecovery: { min: 32, max: 176 },
      };

      let finalMin = convertedMin;
      let finalMax = convertedMax;

      if (newUnit && baseUnit && newUnit !== baseUnit) {
        const override = nonBaseOverrideMap[field.name];
        if (override) {
          finalMin = override.min;
          finalMax = override.max;
        }
      }

      convertedMinMax[field.name] = { min: finalMin, max: finalMax };
      this.updateValidators(field.name, finalMin, finalMax, form);
    });
  }

  onUnitChange(event: Event, field: Field, form: FormGroup, context: UnitChangeContext): void {
    const { selectedUnits, originalMinMax, convertedMinMax } = context;
    const newUnit = event?.target?.['value'];
    const valueType = field?.category;
    const baseUnit = field?.baseUnit;
    const currentValue = parseFloat(form.get(field.name)?.value);

    if (!newUnit || !valueType || !baseUnit || isNaN(currentValue)) {
      return;
    }

    if (selectedUnits[field.name] === baseUnit) {
      // Direct conversion from base unit to new unit
      this.unitConversionService
        .convert(currentValue, baseUnit, newUnit, valueType)
        .pipe(
          catchError((error) => {
            this.notificationService.showNotification(NotificationType.Error, 'An error occurred during unit conversion.');
            return of(currentValue);
          }),
        )
        .subscribe((convertedValue) => {
          // Apply converted value and refresh min/max validators
          this.applyConvertedValueAndUpdate({
            field,
            form,
            selectedUnits,
            newUnit,
            convertedValue,
            originalMinMax,
            convertedMinMax,
            baseUnit,
            valueType,
          });
        });
    } else {
      // Convert from current unit to base unit first, then to new unit
      this.unitConversionService
        .convert(currentValue, selectedUnits[field.name], baseUnit, valueType)
        .pipe(
          catchError(() => of(currentValue)),
          switchMap((valueInBaseUnit) =>
            this.unitConversionService.convert(valueInBaseUnit, baseUnit, newUnit, valueType).pipe(catchError(() => of(valueInBaseUnit))),
          ),
        )
        .subscribe((convertedValue) => {
          // Apply converted value and refresh min/max validators
          this.applyConvertedValueAndUpdate({
            field,
            form,
            selectedUnits,
            newUnit,
            convertedValue,
            originalMinMax,
            convertedMinMax,
            baseUnit,
            valueType,
          });
        });
    }
  }

  // Helper used by onUnitChange subscribe handlers to apply the converted
  // numeric value, update the selectedUnits map and refresh validators.
  private applyConvertedValueAndUpdate(args: {
    field: Field;
    form: FormGroup;
    selectedUnits: { [key: string]: string };
    newUnit: string;
    convertedValue: number;
    originalMinMax: MinMaxValues;
    convertedMinMax: MinMaxValues;
    baseUnit: string;
    valueType: string;
  }): void {
    const { field, form, selectedUnits, newUnit, convertedValue, originalMinMax, convertedMinMax, baseUnit, valueType } = args;
    selectedUnits[field.name] = newUnit;
    form.get(field.name)?.setValue(parseFloat(convertedValue.toString()));
    this.convertMinMax({ field, baseUnit, newUnit, valueType, originalMinMax, convertedMinMax, form });
  }
}
